import { Button2, Input, ModalWindow, Type as T } from "@infobis/ui-styles-utils";
import st from './applicationForm.module.scss';
import { useTypedSelector } from "@hooks/useTypedSelector";
import { FC, useRef, useState } from "react";
import LoadingBox from "@elements/loading/LoadingBox";
import { useUpdateEffect } from "usehooks-ts";

interface ApplicationFormProps {
    hideWindow: () => void
    sendRequest: () => Promise<void>
    postStatus: {success: boolean | null, loading: boolean}
    setPostStatus: React.Dispatch<React.SetStateAction<{success: boolean | null, loading: boolean}>>
    isLoading: boolean
    children: (isLoading: boolean) => JSX.Element
}

const ApplicationForm: FC<ApplicationFormProps> = ({hideWindow, sendRequest, postStatus, setPostStatus, isLoading, children}) => {

    const sendData = async () => {
        setPostStatus((state) => ({...state, loading: true}))
        try {
            await sendRequest();
            setPostStatus({success: true, loading: false});
        } catch (e) {
            setPostStatus({success: false, loading: false});
        }
    };

    const prevPostStatusRef = useRef<boolean | null>(postStatus.success);

    useUpdateEffect(() => {
        if (!postStatus.loading && prevPostStatusRef.current) {
            hideWindow();
        }
        prevPostStatusRef.current = postStatus.success;
    }, [postStatus.success]);

    return (
        <div className={st['application-form-wrp']}>
            {(isLoading) && (
                <LoadingBox
                    style={{
                        backgroundColor: 'transparent',
                        position: 'absolute',
                        alignSelf: 'center',
                        marginTop: '-46px',
                        height: 'unset',
                        width: 'unset',
                    }}
                />
            )}
            <Input.BaseForm>
                {({isSubmitDisabled, handleSubmit }) => {
                    return (
                        <div
                            style={{opacity: isLoading ? 0 : 1, pointerEvents: isLoading ? 'none' : 'auto'}}
                            className={st['application-form']}
                        >
                            {children(isLoading)}
                            <Button2.Primary
                                onClick={handleSubmit(() => sendData())}
                                disabled={isSubmitDisabled}
                                className={st['application-form__submit']}
                                size={T.Size.MEDIUM}
                            >
                                {'Отправить заявку'}
                            </Button2.Primary>
                        </div>
                    )
                }}
            </Input.BaseForm>
            {(!postStatus.loading && postStatus.success !== null) && (
                <ModalWindow
                    headingText={
                        postStatus.success
                            ? 'Заявка принята'
                            : 'Возникла ошибка'
                    }
                    text={
                        postStatus.success
                            ? 'Заявка принята. Ожидайте звонка специалиста.'
                            : 'Возникла ошибка. Обратитесь, пожалуйста, в техническую поддержку по номеру 8 800 234 07 44.'
                    }
                    confirmText={'ОК'}
                    confirmAction={() => setPostStatus({success: null, loading: false})}
                    hideWindow={() => setPostStatus({success: null, loading: false})}
                />
            )}
        </div>
    );
};

interface WrapperAppForm extends Omit<ApplicationFormProps, 'postStatus' | 'setPostStatus' | 'isLoading'> {
    style?: React.CSSProperties
    headingText: string
}

const WrappedAppForm: FC<WrapperAppForm> = ({
    style,
    headingText,
    hideWindow,
    sendRequest,
    children,
}) => {

    const [postStatus, setPostStatus] = useState<{success: boolean | null, loading: boolean}>({
        success: null,
        loading: false
    });

    const {operations, goods: {goods, goodsGroups, goodsCategories}, users, geoZones} = useTypedSelector(state => state);

    const isLoading = [operations, goods, goodsGroups, goodsCategories, users, geoZones].some(({isApiLoading}) => isApiLoading) || postStatus.loading;

    return (
        <ModalWindow
            hideWindow={() => {
                hideWindow()
            }}
            outsideClickAction={() => {
                hideWindow()
            }}
            headingText={headingText}
            style={{
                width: '600px',
                display: 'flex',
                justifyContent: 'center',
                maxHeight: '100%',
                fontFamily: 'PTRootUI',
                height: isLoading ? '250px' : 'unset',
                ...style,
            }}
            overlayStyle={{
                maxHeight: '100%',
                padding: '24px',
            }}
        >
            <ApplicationForm
                hideWindow={hideWindow}
                sendRequest={sendRequest}
                postStatus={postStatus}
                setPostStatus={setPostStatus}
                isLoading={isLoading}
                children={children}
            />
        </ModalWindow>
    )
};

export default WrappedAppForm;